import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslate } from 'react-admin';
import { Typography, Box, CircularProgress } from '@mui/material';
import BaseWidget from './base';
import { DashboardWidget, SchemaWidgetConfig, DataSourceItem } from '../../../types/dashboard.ui';
import { dashboardAPI } from '../../../api/dashboard';

interface SchemaWidgetProps {
  widget: DashboardWidget;
  editMode?: boolean;
  onEdit?: (widget: DashboardWidget) => void;
  onDelete?: (widget: DashboardWidget) => void;
}

interface ParamValue {
  paramId: string;
  paramName: string;
  value: number | string;
  unit?: string;
  timestamp?: string;
  status?: 'online' | 'offline' | 'warning' | 'unknown';
}

const SchemaWidget: React.FC<SchemaWidgetProps> = ({ widget, editMode, onEdit, onDelete }: SchemaWidgetProps) => {
  const translate = useTranslate();
  const [paramValues, setParamValues] = useState<ParamValue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const config = useMemo(() => 
    (typeof widget.config === 'string' ? JSON.parse(widget.config) : widget.config) as SchemaWidgetConfig || {}, 
    [widget.config]
  );

  // Извлекаем источники данных из нового формата
  const dataSources = useMemo<DataSourceItem[]>(() => {
    if (config.dataSource?.items && Array.isArray(config.dataSource.items)) {
      return config.dataSource.items;
    }
    return [];
  }, [config.dataSource]);

  const loadParamData = useCallback(() => {
    if (!dataSources || dataSources.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - 5 * 60 * 1000); // Last 5 minutes
    const paramIds = dataSources.map(ds => ds.paramId);

    dashboardAPI.getHistoricalDataByParams(
      paramIds,
      startTime.toISOString(),
      endTime.toISOString()
    )
      .then((response) => {
        if (!response || response.length === 0) {
          setParamValues([]);
          setLoading(false);
          return;
        }

        const paramMap = new Map(dataSources.map(ds => [ds.paramId, ds]));
        const values: ParamValue[] = [];

        response.forEach((series) => {
          const dataSource = paramMap.get(series.param_id);
          if (!dataSource || !series.values || series.values.length === 0) return;

          // Берем последнее значение
          const lastPoint = series.values[series.values.length - 1];
          
          values.push({
            paramId: series.param_id,
            paramName: dataSource.paramName || dataSource.paramVarname || series.param_id,
            value: lastPoint.value,
            unit: dataSource.paramUnit,
            timestamp: lastPoint.timestamp,
            status: determineStatus(lastPoint.value, lastPoint.timestamp)
          });
        });

        setParamValues(values);
      })
      .catch((err) => {
        console.error('Error loading param data for schema:', err);
        setError(translate('resources.dashboards.widgets.schema.error_loading'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataSources, translate]);

  const determineStatus = (value: any, timestamp: string): 'online' | 'offline' | 'warning' | 'unknown' => {
    // Если данные старше 5 минут - offline
    const dataTime = new Date(timestamp).getTime();
    const now = Date.now();
    if (now - dataTime > 5 * 60 * 1000) {
      return 'offline';
    }

    // Если значение null/undefined - unknown
    if (value === null || value === undefined) {
      return 'unknown';
    }

    return 'online';
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'online':
        return '#22c55e'; // green
      case 'offline':
        return '#ef4444'; // red
      case 'warning':
        return '#f59e0b'; // yellow
      default:
        return '#6b7280'; // gray
    }
  };

  const formatValue = (value: any, unit?: string): string => {
    if (value === null || value === undefined) return '-';
    
    if (typeof value === 'number') {
      return value.toFixed(2) + (unit ? ` ${unit}` : '');
    }
    
    return String(value) + (unit ? ` ${unit}` : '');
  };

  const renderStaticSchema = () => {
    if (config.schemaData) {
      // Render custom schema data (SVG, Canvas, etc.)
      return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {translate('resources.dashboards.widgets.schema.custom_schema')}
            </Typography>
            <Box 
              component="pre" 
              sx={{ 
                fontSize: '0.75rem', 
                bgcolor: 'grey.100', 
                p: 2, 
                borderRadius: 1, 
                overflow: 'auto', 
                maxHeight: 256 
              }}
            >
              {JSON.stringify(config.schemaData, null, 2)}
            </Box>
          </Box>
        </Box>
      );
    }

    // Default schema placeholder with parameter values
    return (
      <Box sx={{ height: '100%', width: '100%', position: 'relative', bgcolor: 'grey.50', borderRadius: 1 }}>
        <svg
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 400 300"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Sample industrial elements */}
          {/* Tank */}
          <rect x="50" y="100" width="80" height="100" fill="#94a3b8" stroke="#475569" strokeWidth="2" rx="5"/>
          <text x="90" y="155" textAnchor="middle" fontSize="12" fill="#1e293b">
            {translate('resources.dashboards.widgets.schema.elements.tank')}
          </text>
          
          {/* Pipe */}
          <rect x="130" y="145" width="140" height="10" fill="#64748b" stroke="#475569" strokeWidth="1"/>
          
          {/* Pump */}
          <circle cx="300" cy="150" r="25" fill="#3b82f6" stroke="#1d4ed8" strokeWidth="2"/>
          <text x="300" y="155" textAnchor="middle" fontSize="12" fill="white">
            {translate('resources.dashboards.widgets.schema.elements.pump')}
          </text>
          
          {/* Valve */}
          <polygon points="180,140 200,140 190,130 190,160" fill="#f59e0b" stroke="#d97706" strokeWidth="2"/>
          <text x="190" y="175" textAnchor="middle" fontSize="12" fill="#92400e">
            {translate('resources.dashboards.widgets.schema.elements.valve')}
          </text>

          {/* Parameter values as status indicators */}
          {paramValues.map((param, index) => {
            const x = 60 + (index * 100);
            const y = 50;
            
            return (
              <g key={param.paramId}>
                <circle 
                  cx={x} 
                  cy={y} 
                  r="10" 
                  fill={getStatusColor(param.status)}
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text 
                  x={x} 
                  y={y - 20} 
                  textAnchor="middle" 
                  fontSize="10" 
                  fill="#374151"
                  fontWeight="bold"
                >
                  {param.paramName}
                </text>
                <text 
                  x={x} 
                  y={y + 25} 
                  textAnchor="middle" 
                  fontSize="12" 
                  fill="#1f2937"
                >
                  {formatValue(param.value, param.unit)}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    );
  };

  useEffect(() => {
    loadParamData();
    
    // Set up auto refresh (30 seconds)
    const interval = setInterval(loadParamData, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [config, loadParamData]);

  const content = (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : dataSources.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography color="textSecondary">
            {translate('resources.dashboards.widgets.schema.no_data_source')}
          </Typography>
        </Box>
      ) : (
        <>
          {renderStaticSchema()}
          
          {/* Legend for parameters */}
          {paramValues.length > 0 && (
            <Box 
              sx={{ 
                position: 'absolute', 
                bottom: 8, 
                left: 8, 
                bgcolor: 'background.paper', 
                borderRadius: 1, 
                p: 1, 
                fontSize: '0.75rem',
                boxShadow: 2,
                maxHeight: '40%',
                overflow: 'auto'
              }}
            >
              <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                {translate('resources.dashboards.widgets.schema.parameters')}:
              </Typography>
              {paramValues.map((param) => (
                <Box key={param.paramId} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: getStatusColor(param.status),
                    }}
                  />
                  <Typography variant="caption" noWrap>
                    {param.paramName}: {formatValue(param.value, param.unit)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <BaseWidget
      widget={widget}
      editMode={editMode}
      onEdit={onEdit ? () => onEdit(widget) : undefined}
      onDelete={onDelete ? () => onDelete(widget) : undefined}
    >
      {content}
    </BaseWidget>
  );
};

export default SchemaWidget;
